import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  formatDate,
  getCurrentDate,
  formatDateForInput,
  parseDateString,
} from "../utils/dateUtils";
import { createNotification } from "../utils/notifications";

function Lessons() {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userEntityId, setUserEntityId] = useState(null); // Candidate document ID
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [viewingLesson, setViewingLesson] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterInstructor, setFilterInstructor] = useState("");
  const [filterCandidate, setFilterCandidate] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const itemsPerPage = 30;

  const [formData, setFormData] = useState({
    candidateId: "",
    hours: "",
    hoursType: "normal", // "normal" or "bonus"
    date: getCurrentDate(),
    timeStarted: "",
    timeEnded: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [candidateSearchQuery, setCandidateSearchQuery] = useState("");
  const [isCandidateDropdownOpen, setIsCandidateDropdownOpen] = useState(false);

  // Fetch candidates
  const fetchCandidates = useCallback(async () => {
    try {
      const candidatesSnapshot = await getDocs(
        query(collection(db, "candidates"), orderBy("firstName", "asc"))
      );
      const candidatesData = candidatesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCandidates(candidatesData);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      showToast(t("lessons.toast.fetchError"), "error");
    }
  }, [t]);

  // Fetch instructors
  const fetchInstructors = useCallback(async () => {
    try {
      const instructorsSnapshot = await getDocs(
        query(collection(db, "instructors"), orderBy("firstName", "asc"))
      );
      const instructorsData = instructorsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInstructors(instructorsData);
    } catch (error) {
      console.error("Error fetching instructors:", error);
    }
  }, []);

  // Fetch lessons
  const fetchLessons = useCallback(async () => {
    try {
      const lessonsSnapshot = await getDocs(
        query(collection(db, "lessons"), orderBy("createdAt", "desc"))
      );
      const lessonsData = lessonsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLessons(lessonsData);
    } catch (error) {
      console.error("Error fetching lessons:", error);
      showToast(t("lessons.toast.fetchLessonsError"), "error");
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Fetch user role and entityId
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || null);
            setUserEntityId(userData.entityId || null);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUserRole(null);
        setUserEntityId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      // Fetch all data in parallel for better performance
      await Promise.all([
        fetchCandidates(),
        fetchInstructors(),
        fetchLessons(),
      ]);
    };
    loadData();
  }, [fetchCandidates, fetchInstructors, fetchLessons]);

  // Show toast notification
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Create lookup maps for O(1) access instead of O(n) array.find()
  const candidatesMap = useMemo(() => {
    const map = new Map();
    candidates.forEach((candidate) => {
      map.set(candidate.id, candidate);
    });
    return map;
  }, [candidates]);

  const instructorsMap = useMemo(() => {
    const map = new Map();
    instructors.forEach((instructor) => {
      map.set(instructor.id, instructor);
    });
    return map;
  }, [instructors]);

  // Get selected candidate (optimized with Map)
  const selectedCandidate = useMemo(() => {
    return formData.candidateId
      ? candidatesMap.get(formData.candidateId) || null
      : null;
  }, [formData.candidateId, candidatesMap]);

  // Filtered candidates for search
  const filteredCandidates = useMemo(() => {
    if (!candidateSearchQuery.trim()) {
      return candidates;
    }
    const query = candidateSearchQuery.toLowerCase().trim();
    return candidates.filter((candidate) => {
      const firstName = (candidate.firstName || "").toLowerCase();
      const lastName = (candidate.lastName || "").toLowerCase();
      const candidateId = (candidate.candidateId || "").toLowerCase();
      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        candidateId.includes(query) ||
        `${firstName} ${lastName}`.includes(query)
      );
    });
  }, [candidates, candidateSearchQuery]);

  // Check if lesson is within 5 days
  const isWithin5Days = (lesson) => {
    if (!lesson.createdAt) return false;
    const createdAt = lesson.createdAt.toDate
      ? lesson.createdAt.toDate()
      : new Date(lesson.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - createdAt);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 5;
  };

  // Helper function to format hours (remove .0 for whole numbers)
  const formatHours = (hours) => {
    const num = parseFloat(hours) || 0;
    // Always return whole number
    return Math.round(num).toString();
  };

  // Calculate remaining hours
  const getRemainingHours = () => {
    if (!selectedCandidate) return { normal: 0, bonus: 0 };

    const totalHours = parseFloat(selectedCandidate.totalHours) || 0;
    const completedHours = parseFloat(selectedCandidate.completedHours) || 0;
    const bonusHours = parseFloat(selectedCandidate.bonusHours) || 0;
    const completedBonusHours =
      parseFloat(selectedCandidate.completedBonusHours) || 0;

    const remainingNormal = Math.max(0, totalHours - completedHours);
    const remainingBonus = Math.max(0, bonusHours - completedBonusHours);

    return { normal: remainingNormal, bonus: remainingBonus };
  };

  // Helper function to get instructor name by ID (optimized with Map)
  const getInstructorNameById = useCallback(
    (instructorId) => {
      if (!instructorId) return null;
      const instructor = instructorsMap.get(instructorId);
      return instructor
        ? `${instructor.firstName} ${instructor.lastName}`
        : null;
    },
    [instructorsMap]
  );

  // Get candidate by ID (optimized with Map)
  const getCandidateById = useCallback(
    (candidateId) => {
      return candidatesMap.get(candidateId) || null;
    },
    [candidatesMap]
  );

  // Pre-process lessons with parsed dates and instructor info (memoized)
  const processedLessons = useMemo(() => {
    return lessons.map((lesson) => {
      const lessonDate = lesson.date ? new Date(lesson.date) : null;
      const candidate = candidatesMap.get(lesson.candidateId);
      // Use instructorId from lesson if available (for tracking), otherwise fall back to candidate's current instructor
      const instructorId =
        lesson.instructorId || candidate?.assignedInstructorId;
      const instructor = instructorId ? instructorsMap.get(instructorId) : null;
      const instructorName = instructor
        ? `${instructor.firstName} ${instructor.lastName}`
        : null;

      return {
        ...lesson,
        parsedDate:
          lessonDate && !isNaN(lessonDate.getTime()) ? lessonDate : null,
        dateOnly:
          lessonDate && !isNaN(lessonDate.getTime())
            ? new Date(
                lessonDate.getFullYear(),
                lessonDate.getMonth(),
                lessonDate.getDate()
              )
            : null,
        hours: parseFloat(lesson.hours) || 0,
        instructorName,
        year: lessonDate ? lessonDate.getFullYear() : null,
        month: lessonDate ? lessonDate.getMonth() + 1 : null,
      };
    });
  }, [lessons, candidatesMap, instructorsMap]);

  // Get available years from lessons (based on date) - optimized
  const availableYears = useMemo(() => {
    const years = new Set();
    processedLessons.forEach((lesson) => {
      if (lesson.year) {
        years.add(lesson.year);
      }
    });
    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  }, [processedLessons]);

  // Get available months for selected year - optimized
  const availableMonths = useMemo(() => {
    if (selectedYear === "all") return [];

    const months = new Set();
    const selectedYearNum = parseInt(selectedYear);
    processedLessons.forEach((lesson) => {
      if (lesson.year === selectedYearNum && lesson.month) {
        months.add(lesson.month);
      }
    });
    return Array.from(months).sort((a, b) => a - b); // Sort ascending (1-12)
  }, [processedLessons, selectedYear]);

  // Reset month when year changes to "all"
  useEffect(() => {
    if (selectedYear === "all") {
      setSelectedMonth("all");
    }
  }, [selectedYear]);

  // Calculate stats (optimized - single pass through lessons)
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // Week = Monday–Sunday (ISO); getDay() 0=Sun, 1=Mon → Monday offset = (getDay() + 6) % 7
    const thisWeek = new Date(today);
    thisWeek.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    thisWeek.setHours(0, 0, 0, 0);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisYear = new Date(now.getFullYear(), 0, 1);

    // Determine the month range for stats (based on selected month, or current month)
    let selectedMonthStart, selectedMonthEnd;
    if (selectedYear !== "all" && selectedMonth !== "all") {
      // Use selected month
      selectedMonthStart = new Date(
        parseInt(selectedYear),
        parseInt(selectedMonth) - 1,
        1
      );
      selectedMonthEnd = new Date(
        parseInt(selectedYear),
        parseInt(selectedMonth) - 1,
        new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate(),
        23,
        59,
        59
      );
    } else {
      // Use current month
      selectedMonthStart = thisMonth;
      selectedMonthEnd = now;
    }

    // Determine the year range for stats (always the whole year, not just selected month)
    let selectedYearStart, selectedYearEnd;
    if (selectedYear === "all") {
      selectedYearStart = thisYear;
      selectedYearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
    } else {
      // Always use the whole year
      selectedYearStart = new Date(parseInt(selectedYear), 0, 1);
      selectedYearEnd = new Date(parseInt(selectedYear), 11, 31, 23, 59, 59);
    }

    // Filter lessons based on user role for stats calculation
    let lessonsForStats = processedLessons;
    if (userRole === "candidate" && userEntityId) {
      lessonsForStats = processedLessons.filter(
        (lesson) => lesson.candidateId === userEntityId
      );
    } else if (userRole === "instructor" && userEntityId) {
      lessonsForStats = processedLessons.filter((lesson) => {
        const candidate = candidatesMap.get(lesson.candidateId);
        return candidate && candidate.assignedInstructorId === userEntityId;
      });
    }

    let todayHours = 0;
    let weekHours = 0;
    let monthHours = 0;
    let yearHours = 0;
    let allTimeHours = 0;

    const todayInstructors = {};
    const weekInstructors = {};
    const monthInstructors = {};
    const yearInstructors = {};
    const allTimeInstructors = {};

    // Single pass through filtered lessons for stats
    for (const lesson of lessonsForStats) {
      if (!lesson.parsedDate) continue;

      const { hours, instructorName, parsedDate, dateOnly, year, month } =
        lesson;

      // Filter by year/month if needed
      if (selectedYear !== "all" && year !== parseInt(selectedYear)) continue;
      if (
        selectedYear !== "all" &&
        selectedMonth !== "all" &&
        month !== parseInt(selectedMonth)
      )
        continue;

      // Today
      if (dateOnly && dateOnly.getTime() === today.getTime()) {
        todayHours += hours;
        if (instructorName) {
          todayInstructors[instructorName] =
            (todayInstructors[instructorName] || 0) + hours;
        }
      }

      // This Week
      if (parsedDate >= thisWeek && parsedDate <= now) {
        weekHours += hours;
        if (instructorName) {
          weekInstructors[instructorName] =
            (weekInstructors[instructorName] || 0) + hours;
        }
      }

      // Selected Month (or current month if no month selected)
      if (parsedDate >= selectedMonthStart && parsedDate <= selectedMonthEnd) {
        monthHours += hours;
        if (instructorName) {
          monthInstructors[instructorName] =
            (monthInstructors[instructorName] || 0) + hours;
        }
      }

      // Selected Year / This Year (always whole year)
      if (parsedDate >= selectedYearStart && parsedDate <= selectedYearEnd) {
        yearHours += hours;
        if (instructorName) {
          yearInstructors[instructorName] =
            (yearInstructors[instructorName] || 0) + hours;
        }
      }

      // All Time
      allTimeHours += hours;
      if (instructorName) {
        allTimeInstructors[instructorName] =
          (allTimeInstructors[instructorName] || 0) + hours;
      }
    }

    // Helper to convert object to sorted array
    const sortInstructors = (instructorsObj) => {
      const entries = Object.entries(instructorsObj);
      return entries
        .map(([name, hours]) => ({ name, hours }))
        .sort((a, b) => b.hours - a.hours);
    };

    return {
      today: {
        total: todayHours,
        instructors: sortInstructors(todayInstructors),
      },
      week: {
        total: weekHours,
        instructors: sortInstructors(weekInstructors),
      },
      month: {
        total: monthHours,
        instructors: sortInstructors(monthInstructors),
      },
      year: {
        total: yearHours,
        instructors: sortInstructors(yearInstructors),
      },
      allTime: {
        total: allTimeHours,
        instructors: sortInstructors(allTimeInstructors),
      },
    };
  }, [
    processedLessons,
    selectedYear,
    selectedMonth,
    userRole,
    userEntityId,
    candidatesMap,
  ]);

  // Calculate instructor stats (optimized with Map lookups)
  const instructorStats = useMemo(() => {
    const statsMap = new Map();

    // Initialize all instructors
    instructors.forEach((instructor) => {
      statsMap.set(instructor.id, {
        instructorId: instructor.id,
        instructorName: `${instructor.firstName} ${instructor.lastName}`,
        registeredHours: 0,
        completedHours: 0,
        remainingHours: 0,
      });
    });

    // Calculate registered and completed hours for each instructor
    candidates.forEach((candidate) => {
      if (candidate.assignedInstructorId) {
        const instructorId = candidate.assignedInstructorId;
        let stat = statsMap.get(instructorId);

        if (!stat) {
          const instructor = instructorsMap.get(instructorId);
          if (instructor) {
            stat = {
              instructorId: instructorId,
              instructorName: `${instructor.firstName} ${instructor.lastName}`,
              registeredHours: 0,
              completedHours: 0,
              remainingHours: 0,
            };
            statsMap.set(instructorId, stat);
          }
        }

        if (stat) {
          const totalHours = parseFloat(candidate.totalHours) || 0;
          const bonusHours = parseFloat(candidate.bonusHours) || 0;
          const completedHours = parseFloat(candidate.completedHours) || 0;
          const completedBonusHours =
            parseFloat(candidate.completedBonusHours) || 0;

          stat.registeredHours += totalHours + bonusHours;
          stat.completedHours += completedHours + completedBonusHours;
        }
      }
    });

    // Calculate remaining hours and filter
    const result = [];
    statsMap.forEach((stat) => {
      stat.remainingHours = stat.registeredHours - stat.completedHours;
      if (stat.registeredHours > 0) {
        result.push(stat);
      }
    });

    return result;
  }, [candidates, instructors, instructorsMap]);

  // Pre-process date filters once
  const dateFilters = useMemo(() => {
    let fromDate = null;
    let toDate = null;

    if (filterDateFrom) {
      const parsed = parseDateString(filterDateFrom);
      if (parsed) {
        fromDate = new Date(parsed);
        fromDate.setHours(0, 0, 0, 0);
      }
    }

    if (filterDateTo) {
      const parsed = parseDateString(filterDateTo);
      if (parsed) {
        toDate = new Date(parsed);
        toDate.setHours(23, 59, 59, 999);
      }
    }

    return { fromDate, toDate };
  }, [filterDateFrom, filterDateTo]);

  // Filter and search lessons (optimized with pre-processed data)
  const filteredLessons = useMemo(() => {
    let filtered = processedLessons;

    // For candidates, only show their own lessons
    if (userRole === "candidate" && userEntityId) {
      filtered = processedLessons.filter(
        (lesson) => lesson.candidateId === userEntityId
      );
    }

    // For instructors, only show lessons for their assigned candidates
    if (userRole === "instructor" && userEntityId) {
      filtered = processedLessons.filter((lesson) => {
        const candidate = candidatesMap.get(lesson.candidateId);
        return candidate && candidate.assignedInstructorId === userEntityId;
      });
    }

    const searchLower = searchQuery.toLowerCase();

    return filtered.filter((lesson) => {
      // Search filter (only for admin/instructor)
      if (userRole !== "candidate" && searchQuery) {
        const matchesSearch =
          lesson.candidateName?.toLowerCase().includes(searchLower) ||
          lesson.candidateId?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Instructor filter (using pre-processed candidate data) - only for admin/instructor
      if (userRole !== "candidate" && filterInstructor) {
        const candidate = candidatesMap.get(lesson.candidateId);
        if (!candidate || candidate.assignedInstructorId !== filterInstructor) {
          return false;
        }
      }

      // Candidate filter - only for admin/instructor
      if (
        userRole !== "candidate" &&
        filterCandidate &&
        lesson.candidateId !== filterCandidate
      ) {
        return false;
      }

      // Type filter - only for admin/instructor
      if (
        userRole !== "candidate" &&
        filterType &&
        lesson.hoursType !== filterType
      ) {
        return false;
      }

      // Date range filter (using pre-parsed dates) - only for admin/instructor
      if (
        userRole !== "candidate" &&
        (dateFilters.fromDate || dateFilters.toDate)
      ) {
        if (!lesson.dateOnly) return false;

        if (dateFilters.fromDate && lesson.dateOnly < dateFilters.fromDate) {
          return false;
        }

        if (dateFilters.toDate && lesson.dateOnly > dateFilters.toDate) {
          return false;
        }
      }

      return true;
    });
  }, [
    processedLessons,
    userRole,
    userEntityId,
    searchQuery,
    filterInstructor,
    filterCandidate,
    filterType,
    dateFilters,
    candidatesMap,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredLessons.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLessons = filteredLessons.slice(startIndex, endIndex);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    filterInstructor,
    filterCandidate,
    filterType,
    filterDateFrom,
    filterDateTo,
  ]);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Handle blur
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });
  };

  // Handle view
  const handleView = (lesson) => {
    setViewingLesson(lesson);
    setIsViewModalOpen(true);
  };

  // Handle add new lesson
  const handleAddNew = () => {
    setEditingLesson(null);
    setFormData({
      candidateId: "",
      hours: "",
      hoursType: "normal",
      date: getCurrentDate(),
      timeStarted: "",
      timeEnded: "",
      notes: "",
    });
    setErrors({});
    setTouched({});
    setCandidateSearchQuery("");
    setIsCandidateDropdownOpen(false);
    setIsModalOpen(true);
  };

  // Handle edit
  const handleEdit = (lesson) => {
    setEditingLesson(lesson);
    const candidate = candidatesMap.get(lesson.candidateId);
    if (candidate) {
      // Determine the type from the lesson's hoursType
      let hoursType = "normal";
      if (lesson.hoursType === "bonus") {
        hoursType = "bonus";
      } else if (lesson.hoursType === "normalAndBonus") {
        // For normalAndBonus, default to normal but user can change
        hoursType = "normal";
      }

      setFormData({
        candidateId: lesson.candidateId,
        hours: lesson.hours.toString(),
        hoursType: hoursType,
        date: lesson.date || getCurrentDate(),
        timeStarted: lesson.timeStarted || "",
        timeEnded: lesson.timeEnded || "",
        notes: lesson.notes || "",
      });
    }
    setCandidateSearchQuery("");
    setIsCandidateDropdownOpen(false);
    setIsModalOpen(true);
  };

  // Handle delete
  const handleDelete = (lesson) => {
    setLessonToDelete(lesson);
    setIsDeleteModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLesson(null);
    setFormData({
      candidateId: "",
      hours: "",
      hoursType: "normal",
      date: getCurrentDate(),
      timeStarted: "",
      timeEnded: "",
      notes: "",
    });
    setErrors({});
    setTouched({});
    setCandidateSearchQuery("");
    setIsCandidateDropdownOpen(false);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.candidateId) {
      newErrors.candidateId = t("lessons.errors.candidateRequired");
    }

    if (!formData.hoursType) {
      newErrors.hoursType = t("lessons.errors.hoursTypeRequired");
    }

    if (!formData.hours || parseFloat(formData.hours) <= 0) {
      newErrors.hours = t("lessons.errors.hoursRequired");
    }

    if (!formData.date) {
      newErrors.date = t("lessons.errors.dateRequired");
    }

    // Time fields are optional - no validation needed

    // Validate hours don't exceed remaining for selected type
    if (selectedCandidate && formData.hours) {
      const hoursToAdd = parseFloat(formData.hours);
      const remaining = getRemainingHours();

      if (formData.hoursType === "normal") {
        if (hoursToAdd > remaining.normal) {
          newErrors.hours = t("lessons.errors.hoursExceedRemaining", {
            remaining: remaining.normal,
          });
        }
        // Check if candidate has no normal hours left
        if (remaining.normal === 0) {
          newErrors.candidateId = t("lessons.errors.noHoursLeft");
        }
      } else if (formData.hoursType === "bonus") {
        if (hoursToAdd > remaining.bonus) {
          newErrors.hours = t("lessons.errors.hoursExceedRemaining", {
            remaining: remaining.bonus,
          });
        }
        // Check if candidate has no bonus hours left
        if (remaining.bonus === 0) {
          newErrors.candidateId = t("lessons.errors.noHoursLeft");
        }
      }
    }

    // Validate time ended is after time started
    if (formData.timeStarted && formData.timeEnded) {
      const [startHour, startMin] = formData.timeStarted.split(":").map(Number);
      const [endHour, endMin] = formData.timeEnded.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (endMinutes <= startMinutes) {
        newErrors.timeEnded = t("lessons.errors.timeEndedAfterStarted");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reverse hours from candidate (for edit/delete)
  const reverseHoursFromCandidate = async (candidate, lesson) => {
    const candidateRef = doc(db, "candidates", candidate.id);
    const updateData = {};
    const oldHours = parseFloat(lesson.hours) || 0;
    const oldType = lesson.hoursType || "normal";
    const totalHours = parseFloat(candidate.totalHours) || 0;
    const currentCompleted = parseFloat(candidate.completedHours) || 0;
    const currentBonusCompleted =
      parseFloat(candidate.completedBonusHours) || 0;

    if (oldType === "normal") {
      updateData.completedHours = Math.max(0, currentCompleted - oldHours);
    } else if (oldType === "bonus") {
      updateData.completedBonusHours = Math.max(
        0,
        currentBonusCompleted - oldHours
      );
    } else if (oldType === "normalAndBonus") {
      // For normalAndBonus: reverse from bonus first, then normal
      // Calculate how much went to bonus (excess over totalHours)
      const normalPart = Math.min(oldHours, totalHours);
      const bonusPart = Math.max(0, oldHours - normalPart);

      // Reverse bonus first
      updateData.completedBonusHours = Math.max(
        0,
        currentBonusCompleted - bonusPart
      );
      // Then reverse normal
      updateData.completedHours = Math.max(0, currentCompleted - normalPart);
    }

    await updateDoc(candidateRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
  };

  // Add hours to candidate based on selected type
  const addHoursToCandidate = async (candidate, hoursToAdd, hoursType) => {
    const candidateRef = doc(db, "candidates", candidate.id);
    const totalHours = parseFloat(candidate.totalHours) || 0;
    const completedHours = parseFloat(candidate.completedHours) || 0;
    const bonusHours = parseFloat(candidate.bonusHours) || 0;
    const completedBonusHours = parseFloat(candidate.completedBonusHours) || 0;

    let updateData = {};

    if (hoursType === "normal") {
      // Add to normal hours only
      updateData.completedHours = Math.min(
        completedHours + hoursToAdd,
        totalHours
      );
    } else if (hoursType === "bonus") {
      // Add to bonus hours only
      updateData.completedBonusHours = Math.min(
        completedBonusHours + hoursToAdd,
        bonusHours
      );
    }

    await updateDoc(candidateRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });

    return hoursType;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t("lessons.toast.fillRequired"), "error");
      return;
    }

    if (!selectedCandidate) {
      showToast(t("lessons.toast.candidateNotFound"), "error");
      return;
    }

    try {
      const hoursToAdd = parseFloat(formData.hours);

      // If editing, reverse old hours first
      if (editingLesson) {
        await reverseHoursFromCandidate(selectedCandidate, editingLesson);
        // Refresh candidate data (candidatesMap will be updated automatically via useMemo)
        await fetchCandidates();
      }

      // Check remaining hours for selected type after reversal
      const remaining = getRemainingHours();

      if (formData.hoursType === "normal" && hoursToAdd > remaining.normal) {
        showToast(
          t("lessons.errors.hoursExceedRemaining", {
            remaining: remaining.normal,
          }),
          "error"
        );
        return;
      }

      if (formData.hoursType === "bonus" && hoursToAdd > remaining.bonus) {
        showToast(
          t("lessons.errors.hoursExceedRemaining", {
            remaining: remaining.bonus,
          }),
          "error"
        );
        return;
      }

      // Add new hours with selected type
      const hoursType = await addHoursToCandidate(
        selectedCandidate,
        hoursToAdd,
        formData.hoursType
      );

      if (editingLesson) {
        // Update lesson entry
        const lessonRef = doc(db, "lessons", editingLesson.id);
        await updateDoc(lessonRef, {
          instructorId: selectedCandidate.assignedInstructorId || null, // Update instructor ID if changed
          hours: hoursToAdd,
          hoursType: hoursType,
          date: formData.date,
          timeStarted: formData.timeStarted,
          timeEnded: formData.timeEnded,
          notes: formData.notes || "",
          updatedAt: serverTimestamp(),
        });
        const lessonName = `${selectedCandidate.firstName} ${selectedCandidate.lastName} - ${hoursToAdd}h`;
        await createNotification(
          "lesson_updated",
          "lesson",
          editingLesson.id,
          lessonName,
          {
            candidateId: selectedCandidate.id,
            recipientId: selectedCandidate.authUid || null, // Include candidate's auth UID so they can mark as read
          }
        );
        showToast(t("lessons.toast.hoursUpdated"), "success");
      } else {
        // Create new lesson entry
        const docRef = await addDoc(collection(db, "lessons"), {
          candidateId: selectedCandidate.id,
          candidateName: `${selectedCandidate.firstName} ${selectedCandidate.lastName}`,
          instructorId: selectedCandidate.assignedInstructorId || null, // Store instructor ID at time of creation
          hours: hoursToAdd,
          hoursType: hoursType,
          date: formData.date,
          timeStarted: formData.timeStarted,
          timeEnded: formData.timeEnded,
          notes: formData.notes || "",
          createdAt: serverTimestamp(),
        });
        const lessonName = `${selectedCandidate.firstName} ${selectedCandidate.lastName} - ${hoursToAdd}h`;
        await createNotification(
          "lesson_added",
          "lesson",
          docRef.id,
          lessonName,
          {
            candidateId: selectedCandidate.id,
            recipientId: selectedCandidate.authUid || null, // Include candidate's auth UID so they can mark as read
          }
        );
        showToast(t("lessons.toast.hoursAdded"), "success");
      }

      handleCloseModal();

      // Refresh candidates and lessons
      await fetchCandidates();
      await fetchLessons();
    } catch (error) {
      console.error("Error saving hours:", error);
      showToast(t("lessons.toast.saveError"), "error");
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!lessonToDelete) return;

    try {
      const candidate = candidatesMap.get(lessonToDelete.candidateId);
      if (candidate) {
        await reverseHoursFromCandidate(candidate, lessonToDelete);
      }

      const lessonName = `${lessonToDelete.candidateName || "Unknown"} - ${
        lessonToDelete.hours
      }h`;
      await deleteDoc(doc(db, "lessons", lessonToDelete.id));
      // candidate variable is already declared above, reuse it
      await createNotification(
        "lesson_deleted",
        "lesson",
        lessonToDelete.id,
        lessonName,
        {
          candidateId: lessonToDelete.candidateId,
          recipientId: candidate?.authUid || null, // Include candidate's auth UID so they can mark as read
        }
      );
      showToast(t("lessons.toast.hoursDeleted"), "success");
      setIsDeleteModalOpen(false);
      setLessonToDelete(null);

      // Refresh candidates and lessons
      await fetchCandidates();
      await fetchLessons();
    } catch (error) {
      console.error("Error deleting lesson:", error);
      showToast(t("lessons.toast.deleteError"), "error");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Toast Notification */}
        {toast.show && (
          <div
            className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
              toast.type === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("lessons.title")}
            </h2>
            <p className="text-gray-600">
              {userRole === "candidate"
                ? t("lessons.subtitleCandidate")
                : t("lessons.subtitle")}
            </p>
          </div>
          {userRole === "admin" && (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleAddNew}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center space-x-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span>{t("lessons.addHours")}</span>
              </button>
            </div>
          )}
        </div>

        {/* Year and Month Selectors - Only for admin */}
        {userRole === "admin" && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm font-medium text-gray-700">
                {t("lessons.stats.filterByYear")}:
              </label>
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setSelectedMonth("all"); // Reset month when year changes
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              >
                <option value="all">{t("lessons.stats.allYears")}</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              {selectedYear !== "all" && (
                <>
                  <label className="text-sm font-medium text-gray-700 ml-2">
                    {t("lessons.stats.filterByMonth")}:
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                  >
                    <option value="all">{t("lessons.stats.allMonths")}</option>
                    {availableMonths.map((month) => (
                      <option key={month} value={month}>
                        {t(`lessons.stats.months.${month}`)}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>
        )}

        {/* Stats Cards - For admin and instructor */}
        {(userRole === "admin" || userRole === "instructor") && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Today */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                {t("lessons.stats.today")}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between pt-2 border-b border-gray-200 pb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {t("lessons.stats.totalHours")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {formatHours(stats.today.total)}
                  </span>
                </div>
                {stats.today.instructors.length > 0 && (
                  <div className="space-y-1 pt-2">
                    {stats.today.instructors.slice(0, 3).map((inst) => (
                      <div
                        key={inst.name}
                        className="flex justify-between text-xs text-gray-600"
                      >
                        <span className="truncate mr-2">{inst.name}</span>
                        <span className="font-medium text-gray-900 whitespace-nowrap">
                          {formatHours(inst.hours)}
                        </span>
                      </div>
                    ))}
                    {stats.today.instructors.length > 3 && (
                      <div className="text-xs text-gray-400 pt-1">
                        +{stats.today.instructors.length - 3}{" "}
                        {t("lessons.stats.more")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* This Week */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                {t("lessons.stats.thisWeek")}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between pt-2 border-b border-gray-200 pb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {t("lessons.stats.totalHours")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {formatHours(stats.week.total)}
                  </span>
                </div>
                {stats.week.instructors.length > 0 && (
                  <div className="space-y-1 pt-2">
                    {stats.week.instructors.slice(0, 3).map((inst) => (
                      <div
                        key={inst.name}
                        className="flex justify-between text-xs text-gray-600"
                      >
                        <span className="truncate mr-2">{inst.name}</span>
                        <span className="font-medium text-gray-900 whitespace-nowrap">
                          {formatHours(inst.hours)}
                        </span>
                      </div>
                    ))}
                    {stats.week.instructors.length > 3 && (
                      <div className="text-xs text-gray-400 pt-1">
                        +{stats.week.instructors.length - 3}{" "}
                        {t("lessons.stats.more")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* This Month */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                {selectedYear !== "all" && selectedMonth !== "all"
                  ? `${t(
                      `lessons.stats.months.${selectedMonth}`
                    )} ${selectedYear}`
                  : t("lessons.stats.thisMonth")}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between pt-2 border-b border-gray-200 pb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {t("lessons.stats.totalHours")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {formatHours(stats.month.total)}
                  </span>
                </div>
                {stats.month.instructors.length > 0 && (
                  <div className="space-y-1 pt-2">
                    {stats.month.instructors.slice(0, 3).map((inst) => (
                      <div
                        key={inst.name}
                        className="flex justify-between text-xs text-gray-600"
                      >
                        <span className="truncate mr-2">{inst.name}</span>
                        <span className="font-medium text-gray-900 whitespace-nowrap">
                          {formatHours(inst.hours)}
                        </span>
                      </div>
                    ))}
                    {stats.month.instructors.length > 3 && (
                      <div className="text-xs text-gray-400 pt-1">
                        +{stats.month.instructors.length - 3}{" "}
                        {t("lessons.stats.more")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* This Year / Selected Year */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                {selectedYear === "all"
                  ? t("lessons.stats.thisYear")
                  : selectedYear}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between pt-2 border-b border-gray-200 pb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {t("lessons.stats.totalHours")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {formatHours(stats.year.total)}
                  </span>
                </div>
                {stats.year.instructors.length > 0 && (
                  <div className="space-y-1 pt-2">
                    {stats.year.instructors.slice(0, 3).map((inst) => (
                      <div
                        key={inst.name}
                        className="flex justify-between text-xs text-gray-600"
                      >
                        <span className="truncate mr-2">{inst.name}</span>
                        <span className="font-medium text-gray-900 whitespace-nowrap">
                          {formatHours(inst.hours)}
                        </span>
                      </div>
                    ))}
                    {stats.year.instructors.length > 3 && (
                      <div className="text-xs text-gray-400 pt-1">
                        +{stats.year.instructors.length - 3}{" "}
                        {t("lessons.stats.more")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* All Time */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                {t("lessons.stats.allTime")}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between pt-2 border-b border-gray-200 pb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {t("lessons.stats.totalHours")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {formatHours(stats.allTime.total)}
                  </span>
                </div>
                {stats.allTime.instructors.length > 0 && (
                  <div className="space-y-1 pt-2">
                    {stats.allTime.instructors.slice(0, 3).map((inst) => (
                      <div
                        key={inst.name}
                        className="flex justify-between text-xs text-gray-600"
                      >
                        <span className="truncate mr-2">{inst.name}</span>
                        <span className="font-medium text-gray-900 whitespace-nowrap">
                          {formatHours(inst.hours)}
                        </span>
                      </div>
                    ))}
                    {stats.allTime.instructors.length > 3 && (
                      <div className="text-xs text-gray-400 pt-1">
                        +{stats.allTime.instructors.length - 3}{" "}
                        {t("lessons.stats.more")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Remaining hours for instructor (their candidates) */}
        {userRole === "instructor" && userEntityId && !loading && (() => {
          const myStat = instructorStats.find((s) => s.instructorId === userEntityId);
          if (!myStat) return null;
          return (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                {t("lessons.stats.myRemainingHours")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="border border-gray-200 rounded p-3">
                  <span className="text-xs text-gray-500 block mb-1">
                    {t("lessons.stats.registeredHours")}
                  </span>
                  <span className="font-bold text-gray-900">
                    {formatHours(myStat?.registeredHours ?? 0)}
                  </span>
                </div>
                <div className="border border-gray-200 rounded p-3">
                  <span className="text-xs text-gray-500 block mb-1">
                    {t("lessons.stats.completedHours")}
                  </span>
                  <span className="font-bold text-green-600">
                    {formatHours(myStat?.completedHours ?? 0)}
                  </span>
                </div>
                <div className="border border-gray-200 rounded p-3">
                  <span className="text-xs text-gray-500 block mb-1">
                    {t("lessons.stats.remainingHours")}
                  </span>
                  <span className="font-bold text-blue-600">
                    {formatHours(myStat?.remainingHours ?? 0)}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Instructor Stats - Only for admin */}
        {userRole === "admin" && !loading && instructorStats.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t("lessons.stats.instructorStats")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {instructorStats.map((stat) => (
                <div
                  key={stat.instructorId}
                  className="border border-gray-200 rounded p-2.5"
                >
                  <h4 className="font-medium text-gray-900 text-xs mb-1.5 truncate">
                    {stat.instructorName}
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        {t("lessons.stats.registeredHours")}
                      </span>
                      <span className="font-medium text-gray-900 text-xs">
                        {formatHours(stat.registeredHours)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        {t("lessons.stats.completedHours")}
                      </span>
                      <span className="font-medium text-green-600 text-xs">
                        {formatHours(stat.completedHours)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                      <span className="text-xs font-medium text-gray-700">
                        {t("lessons.stats.remainingHours")}
                      </span>
                      <span className="font-semibold text-blue-600 text-xs">
                        {formatHours(stat.remainingHours)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search and Filters - Only for admin */}
        {userRole === "admin" && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
            {/* Search Bar Row */}
            <div className="relative">
              <input
                type="text"
                placeholder={t("lessons.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-10 border-2 border-gray-400 rounded-lg bg-gray-50 font-semibold text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-black/25 focus:border-black text-base"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Instructor Filter */}
              <select
                value={filterInstructor}
                onChange={(e) => setFilterInstructor(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              >
                <option value="">{t("lessons.filters.allInstructors")}</option>
                {instructors.map((instructor) => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.firstName} {instructor.lastName}
                  </option>
                ))}
              </select>

              {/* Candidate Filter */}
              <select
                value={filterCandidate}
                onChange={(e) => setFilterCandidate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              >
                <option value="">{t("lessons.filters.allCandidates")}</option>
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.firstName} {candidate.lastName} (
                    {candidate.candidateId})
                  </option>
                ))}
              </select>

              {/* Type Filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              >
                <option value="">{t("lessons.filters.allTypes")}</option>
                <option value="normal">{t("lessons.table.normal")}</option>
                <option value="bonus">{t("lessons.table.bonus")}</option>
                <option value="normalAndBonus">
                  {t("lessons.table.normalAndBonus")}
                </option>
              </select>

              {/* Date From Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t("lessons.filters.dateFrom")}:
                </label>
                <input
                  type="date"
                  value={
                    filterDateFrom
                      ? formatDateForInput(
                          parseDateString(filterDateFrom) || filterDateFrom
                        )
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const date = new Date(value);
                      setFilterDateFrom(formatDate(date));
                    } else {
                      setFilterDateFrom("");
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                />
              </div>

              {/* Date To Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t("lessons.filters.dateTo")}:
                </label>
                <input
                  type="date"
                  value={
                    filterDateTo
                      ? formatDateForInput(
                          parseDateString(filterDateTo) || filterDateTo
                        )
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const date = new Date(value);
                      // Validate that "to date" is not before "from date"
                      if (filterDateFrom) {
                        const fromDate = parseDateString(filterDateFrom);
                        if (fromDate && date < fromDate) {
                          return; // Don't update if invalid
                        }
                      }
                      setFilterDateTo(formatDate(date));
                    } else {
                      setFilterDateTo("");
                    }
                  }}
                  min={
                    filterDateFrom
                      ? formatDateForInput(
                          parseDateString(filterDateFrom) || filterDateFrom
                        )
                      : undefined
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                />
              </div>

              {/* Clear Filters */}
              {(searchQuery ||
                filterInstructor ||
                filterDateFrom ||
                filterDateTo) && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setFilterInstructor("");
                    setFilterDateFrom("");
                    setFilterDateTo("");
                    setSelectedYear("all");
                    setSelectedMonth("all");
                  }}
                  className="ml-auto px-3 py-2 text-sm text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span>{t("lessons.clearAll")}</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Results Count */}
        {!loading && filteredLessons.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600 px-2">
            <span>
              {t("lessons.showing")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredLessons.length > 0
                  ? `${startIndex + 1}-${Math.min(
                      endIndex,
                      filteredLessons.length
                    )}`
                  : 0}
              </span>{" "}
              {t("lessons.of")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredLessons.length}
              </span>{" "}
              {t("lessons.hoursText")}
              {userRole !== "candidate" &&
                filteredLessons.length !== lessons.length && (
                  <span className="text-gray-500">
                    {" "}
                    ({t("lessons.of")} {lessons.length} {t("lessons.total")})
                  </span>
                )}
            </span>
          </div>
        )}

        {/* Lessons Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {filteredLessons.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p>{t("lessons.table.noLessons")}</p>
              {userRole === "admin" && (
                <button
                  onClick={handleAddNew}
                  className="mt-4 text-black hover:underline"
                >
                  {t("lessons.addFirstLesson")}
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lessons.table.candidate")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lessons.table.hours")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lessons.table.timeStarted")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lessons.table.timeEnded")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lessons.table.date")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lessons.table.type")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lessons.table.notes")}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("lessons.table.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedLessons.map((lesson) => {
                    const canEditDelete = isWithin5Days(lesson);
                    return (
                      <tr key={lesson.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {lesson.candidateName}
                            </div>
                            {lesson.instructorName && (
                              <div className="text-sm text-gray-500">
                                {t("lessons.table.instructor")}:{" "}
                                {lesson.instructorName}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatHours(lesson.hours)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {lesson.timeStarted || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {lesson.timeEnded || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(lesson.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                              lesson.hoursType === "normal"
                                ? "bg-blue-100 text-blue-800"
                                : lesson.hoursType === "bonus"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-indigo-100 text-indigo-800"
                            }`}
                          >
                            {t(`lessons.table.${lesson.hoursType}`)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {lesson.notes || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-3">
                            <button
                              onClick={() => handleView(lesson)}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                              title={t("lessons.actions.view")}
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                            {userRole === "admin" && canEditDelete && (
                              <>
                                <button
                                  onClick={() => handleEdit(lesson)}
                                  className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                  title={t("lessons.actions.edit")}
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete(lesson)}
                                  className="text-red-600 hover:text-red-900 transition-colors"
                                  title={t("lessons.actions.delete")}
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredLessons.length > itemsPerPage && (
          <div className="flex items-center justify-center space-x-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.previous")}
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-black text-white border-black"
                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Hours Modal */}
      {isModalOpen && (
        <div className="fixed top-0 left-0 right-0 bottom-0 m-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {editingLesson
                  ? t("lessons.editLesson")
                  : t("lessons.form.title")}
              </h3>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col flex-1 overflow-hidden"
            >
              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-6">
                  {/* Candidate Selection */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("lessons.form.selectCandidate")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={
                          selectedCandidate
                            ? `${selectedCandidate.firstName} ${selectedCandidate.lastName} (${selectedCandidate.candidateId})`
                            : candidateSearchQuery
                        }
                        onChange={(e) => {
                          setCandidateSearchQuery(e.target.value);
                          setIsCandidateDropdownOpen(true);
                          if (selectedCandidate) {
                            setFormData((prev) => ({ ...prev, candidateId: "" }));
                            setTouched((prev) => ({ ...prev, candidateId: false }));
                          }
                        }}
                        onFocus={() => setIsCandidateDropdownOpen(true)}
                        onBlur={() => {
                          setTimeout(() => {
                            setIsCandidateDropdownOpen(false);
                            setTouched((prev) => ({ ...prev, candidateId: true }));
                          }, 200);
                        }}
                        disabled={!!editingLesson}
                        placeholder={t("lessons.form.selectCandidatePlaceholder")}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.candidateId && errors.candidateId
                            ? "border-red-500"
                            : "border-gray-300"
                        } ${
                          editingLesson ? "bg-gray-100 cursor-not-allowed" : ""
                        }`}
                      />
                      {isCandidateDropdownOpen && !editingLesson && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {filteredCandidates.length > 0 ? (
                            filteredCandidates.map((candidate) => (
                              <div
                                key={candidate.id}
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    candidateId: candidate.id,
                                  }));
                                  setCandidateSearchQuery("");
                                  setIsCandidateDropdownOpen(false);
                                  setTouched((prev) => ({
                                    ...prev,
                                    candidateId: true,
                                  }));
                                }}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                {candidate.firstName} {candidate.lastName} (
                                {candidate.candidateId})
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-gray-500 text-sm">
                              {t("lessons.form.noCandidatesFound")}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {touched.candidateId && errors.candidateId && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.candidateId}
                      </p>
                    )}
                  </div>

                  {/* Candidate Hours Info */}
                  {selectedCandidate && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <h4 className="font-semibold text-gray-900">
                        {t("lessons.candidateInfo.title")}
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">
                            {t("lessons.candidateInfo.totalHours")}
                          </p>
                          <p className="font-medium text-gray-900">
                            {selectedCandidate.totalHours || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            {t("lessons.candidateInfo.completedHours")}
                          </p>
                          <p className="font-medium text-gray-900">
                            {selectedCandidate.completedHours || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            {t("lessons.candidateInfo.remainingHours")}
                          </p>
                          <p className="font-medium text-green-600">
                            {getRemainingHours().normal}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            {t("lessons.candidateInfo.bonusHours")}
                          </p>
                          <p className="font-medium text-gray-900">
                            {selectedCandidate.bonusHours || 0} (
                            {t("lessons.candidateInfo.completed")}:{" "}
                            {selectedCandidate.completedBonusHours || 0})
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            {t("lessons.candidateInfo.remainingBonus")}
                          </p>
                          <p className="font-medium text-green-600">
                            {getRemainingHours().bonus}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Hours Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("lessons.form.hoursType")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="hoursType"
                      value={formData.hoursType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                        touched.hoursType && errors.hoursType
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="normal">{t("lessons.form.normal")}</option>
                      <option value="bonus">{t("lessons.form.bonus")}</option>
                    </select>
                    {touched.hoursType && errors.hoursType && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.hoursType}
                      </p>
                    )}
                    {selectedCandidate && (
                      <p className="mt-1 text-xs text-gray-500">
                        {formData.hoursType === "normal"
                          ? t("lessons.form.normalHelper", {
                              remaining: getRemainingHours().normal,
                            })
                          : t("lessons.form.bonusHelper", {
                              remaining: getRemainingHours().bonus,
                            })}
                      </p>
                    )}
                  </div>

                  {/* Hours Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("lessons.form.hours")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="hours"
                      value={formData.hours}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      step="0.5"
                      min="0"
                      placeholder={t("lessons.form.hoursPlaceholder")}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                        touched.hours && errors.hours
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {touched.hours && errors.hours && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.hours}
                      </p>
                    )}
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("lessons.form.date")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                        touched.date && errors.date
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {touched.date && errors.date && (
                      <p className="mt-1 text-xs text-red-500">{errors.date}</p>
                    )}
                  </div>

                  {/* Time Started and Time Ended */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("lessons.form.timeStarted")}{" "}
                        <span className="text-gray-400 text-xs">
                          ({t("common.optional")})
                        </span>
                      </label>
                      <input
                        type="time"
                        name="timeStarted"
                        value={formData.timeStarted}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.timeStarted && errors.timeStarted
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {touched.timeStarted && errors.timeStarted && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.timeStarted}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("lessons.form.timeEnded")}{" "}
                        <span className="text-gray-400 text-xs">
                          ({t("common.optional")})
                        </span>
                      </label>
                      <input
                        type="time"
                        name="timeEnded"
                        value={formData.timeEnded}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.timeEnded && errors.timeEnded
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {touched.timeEnded && errors.timeEnded && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.timeEnded}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("lessons.form.notes")}
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={4}
                      placeholder={t("lessons.form.notesPlaceholder")}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  {editingLesson
                    ? t("lessons.form.updateHours")
                    : t("lessons.form.addHours")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Lesson Modal */}
      {isViewModalOpen && viewingLesson && (
        <div className="fixed top-0 left-0 right-0 bottom-0 m-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {t("lessons.view.title")}
              </h3>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500">
                    {t("lessons.view.candidate")}
                  </p>
                  <p className="font-medium text-gray-900 mt-1">
                    {viewingLesson.candidateName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t("lessons.view.date")}
                  </p>
                  <p className="font-medium text-gray-900 mt-1">
                    {formatDate(viewingLesson.date)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t("lessons.view.hours")}
                  </p>
                  <p className="font-medium text-gray-900 mt-1">
                    {formatHours(viewingLesson.hours)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t("lessons.view.type")}
                  </p>
                  <p className="mt-1">
                    <span
                      className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                        viewingLesson.hoursType === "normal"
                          ? "bg-blue-100 text-blue-800"
                          : viewingLesson.hoursType === "bonus"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-indigo-100 text-indigo-800"
                      }`}
                    >
                      {t(`lessons.table.${viewingLesson.hoursType}`)}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t("lessons.view.timeStarted")}
                  </p>
                  <p className="font-medium text-gray-900 mt-1">
                    {viewingLesson.timeStarted || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t("lessons.view.timeEnded")}
                  </p>
                  <p className="font-medium text-gray-900 mt-1">
                    {viewingLesson.timeEnded || "-"}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">
                    {t("lessons.view.notes")}
                  </p>
                  <p className="font-medium text-gray-900 mt-1">
                    {viewingLesson.notes || "-"}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && lessonToDelete && (
        <div className="fixed top-0 left-0 right-0 bottom-0 m-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t("lessons.delete.title")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("lessons.delete.message", {
                  candidate: lessonToDelete.candidateName,
                  hours: lessonToDelete.hours,
                  date: formatDate(lessonToDelete.date),
                })}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setLessonToDelete(null);
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  {t("common.delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Lessons;
